/**
 * Map / path geometry shared by the web editor (rendering) and the backend
 * (validation + bundle export). Keeping one implementation guarantees the robot
 * receives exactly the curve the technician saw.
 */
/** Image pixel (u right, v down, top-left origin) → map-frame metres. Pass u+0.5/v+0.5 for cell centres. */
export function pixelToWorld(u, v, m) {
    const [ox, oy, yaw] = m.origin;
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    const lx = u * m.resolution;
    const ly = (m.height - v) * m.resolution;
    return { x: ox + c * lx - s * ly, y: oy + s * lx + c * ly };
}
/** Map-frame metres → image pixel (u right, v down). */
export function worldToPixel(x, y, m) {
    const [ox, oy, yaw] = m.origin;
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    const dx = x - ox;
    const dy = y - oy;
    const lx = c * dx + s * dy;
    const ly = -s * dx + c * dy;
    return { u: lx / m.resolution, v: m.height - ly / m.resolution };
}
/** World yaw (CCW, radians) → screen angle (CW because v points down). */
export function worldYawToScreen(theta, m) {
    return -(theta - m.origin[2]);
}
export function screenAngleToWorldYaw(angle, m) {
    return -angle + m.origin[2];
}
export function normalizeAngle(a) {
    let r = a % (2 * Math.PI);
    if (r > Math.PI)
        r -= 2 * Math.PI;
    if (r <= -Math.PI)
        r += 2 * Math.PI;
    return r;
}
function dist(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}
/**
 * Evaluate one centripetal Catmull-Rom segment between p1 and p2 (Barry–Goldman
 * pyramidal form). alpha=0.5 is centripetal: no cusps or self-intersections
 * within a segment, and the curve passes through every control point — which is
 * what technicians expect when they click points on a map.
 */
function catmullRomPoint(p0, p1, p2, p3, alpha, u) {
    const eps = 1e-6;
    const t0 = 0;
    const t1 = t0 + Math.max(Math.pow(dist(p0, p1), alpha), eps);
    const t2 = t1 + Math.max(Math.pow(dist(p1, p2), alpha), eps);
    const t3 = t2 + Math.max(Math.pow(dist(p2, p3), alpha), eps);
    const t = t1 + (t2 - t1) * u;
    const lerp = (a, b, ta, tb) => {
        const w = (t - ta) / (tb - ta);
        return { x: a.x + (b.x - a.x) * w, y: a.y + (b.y - a.y) * w };
    };
    const a1 = lerp(p0, p1, t0, t1);
    const a2 = lerp(p1, p2, t1, t2);
    const a3 = lerp(p2, p3, t2, t3);
    const b1 = lerp(a1, a2, t0, t2);
    const b2 = lerp(a2, a3, t1, t3);
    return lerp(b1, b2, t1, t2);
}
/** Dense polyline through the control points (not yet arc-length uniform). */
export function catmullRomPolyline(points, alpha = 0.5, closed = false, perMetre = 40) {
    const n = points.length;
    if (n < 2)
        return points.map((p) => ({ ...p }));
    const pts = points;
    const get = (i) => {
        if (closed)
            return pts[((i % n) + n) % n];
        if (i < 0) {
            // Reflect to extrapolate a phantom start point.
            const a = pts[0];
            const b = pts[1];
            return { x: 2 * a.x - b.x, y: 2 * a.y - b.y };
        }
        if (i >= n) {
            const a = pts[n - 1];
            const b = pts[n - 2];
            return { x: 2 * a.x - b.x, y: 2 * a.y - b.y };
        }
        return pts[i];
    };
    const segCount = closed ? n : n - 1;
    const out = [];
    for (let i = 0; i < segCount; i++) {
        const p0 = get(i - 1);
        const p1 = get(i);
        const p2 = get(i + 1);
        const p3 = get(i + 2);
        const steps = Math.max(4, Math.ceil(dist(p1, p2) * perMetre));
        for (let s = 0; s < steps; s++) {
            if (dist(p1, p2) < 1e-9) {
                out.push({ ...p1 });
                break;
            }
            out.push(catmullRomPoint(p0, p1, p2, p3, alpha, s / steps));
        }
    }
    out.push({ ...(closed ? pts[0] : pts[n - 1]) });
    return out;
}
export function polylineLength(pts) {
    let l = 0;
    for (let i = 1; i < pts.length; i++)
        l += dist(pts[i - 1], pts[i]);
    return l;
}
/**
 * Resample a path at uniform arc-length `step` and attach headings.
 * `headingMode: "fixed"` keeps a constant yaw (holonomic base crabbing).
 */
export function samplePath(controlPoints, opts = {}) {
    const alpha = opts.alpha ?? 0.5;
    const step = opts.step ?? 0.05;
    const dense = catmullRomPolyline(controlPoints, alpha, opts.closed ?? false);
    if (dense.length < 2)
        return dense.map((p) => ({ x: p.x, y: p.y, yaw: opts.fixedHeading ?? 0, s: 0 }));
    const cum = [0];
    for (let i = 1; i < dense.length; i++)
        cum.push(cum[i - 1] + dist(dense[i - 1], dense[i]));
    const total = cum[cum.length - 1];
    const out = [];
    const sVals = [];
    let j = 1;
    // Index-based stepping avoids float drift; stop short of the end (within 1% of a
    // step) so the final pose is always exactly the last control point.
    const count = Math.ceil(total / step - 0.01);
    for (let k = 0; k < count; k++) {
        const s = k * step;
        while (j < cum.length - 1 && cum[j] < s)
            j++;
        const s0 = cum[j - 1];
        const s1 = cum[j];
        const w = s1 - s0 > 1e-12 ? (s - s0) / (s1 - s0) : 0;
        const a = dense[j - 1];
        const b = dense[j];
        out.push({ x: a.x + (b.x - a.x) * w, y: a.y + (b.y - a.y) * w });
        sVals.push(s);
    }
    // Always end exactly on the last control point.
    const last = dense[dense.length - 1];
    out.push({ ...last });
    sVals.push(total);
    const fixed = opts.headingMode === "fixed";
    return out.map((p, i) => {
        let yaw;
        if (fixed) {
            yaw = opts.fixedHeading ?? 0;
        }
        else {
            const a = out[Math.max(0, i === out.length - 1 ? i - 1 : i)];
            const b = out[Math.min(out.length - 1, i === out.length - 1 ? i : i + 1)];
            yaw = Math.atan2(b.y - a.y, b.x - a.x);
        }
        return { x: p.x, y: p.y, yaw: normalizeAngle(yaw), s: sVals[i] };
    });
}
/** Max absolute heading change per metre along sampled poses — a cheap curvature check. */
export function maxCurvature(samples) {
    let k = 0;
    for (let i = 1; i < samples.length; i++) {
        const ds = samples[i].s - samples[i - 1].s;
        if (ds <= 1e-9)
            continue;
        const dyaw = Math.abs(normalizeAngle(samples[i].yaw - samples[i - 1].yaw));
        k = Math.max(k, dyaw / ds);
    }
    return k;
}
/**
 * Occupancy lookup over a decoded map image (8-bit grey, row 0 = top).
 * Returns "occupied" | "free" | "unknown" using Nav2 trinary semantics.
 */
export function classifyPixel(value, negate, occupiedThresh, freeThresh) {
    const p = negate ? value / 255 : (255 - value) / 255;
    if (p > occupiedThresh)
        return "occupied";
    if (p < freeThresh)
        return "free";
    return "unknown";
}
//# sourceMappingURL=geometry.js.map